import { CheckCircle, Phone } from "lucide-react";
import HeroCallbackForm from "@/components/home/HeroCallbackForm";

type ServiceCallbackBandProps = {
  /** Human label used in the headline, e.g. "Warehouse Automation" */
  label: string;
  /** ERP service_interest taxonomy value to preselect in the form */
  interest: string;
  /** Unique partial-save key for this page */
  formKey: string;
};

/**
 * Service landing page conversion band: sits directly under the hero so
 * visitors can request a specialist call without leaving the page. The
 * service interest arrives preselected, producing pre-qualified leads.
 */
const ServiceCallbackBand = ({ label, interest, formKey }: ServiceCallbackBandProps) => {
  return (
    <section className="relative bg-secondary py-14 md:py-16">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-center max-w-6xl mx-auto">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
              Talk to {/^[AEIOU]/i.test(label) ? "an" : "a"} {label} Specialist
            </h2>
            <p className="text-lg text-white/75 mb-7 max-w-xl">
              Skip the RFP maze. Tell us where to reach you and a specialist calls within
              one business day with real scope, real numbers, and a real plan.
            </p>
            <div className="space-y-3 mb-7">
              {[
                "Scope validated and budget pressure-tested before you commit",
                "One accountable partner: design, build, install, and support",
                "Turnkey delivery from under $100K to $100M+",
              ].map((point) => (
                <div key={point} className="flex items-start gap-2.5 text-white/85">
                  <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
            <a
              href="tel:+13054337789"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold transition-colors"
            >
              <Phone className="h-4 w-4 text-primary" /> Prefer to talk now? (305) 433-7789
            </a>
          </div>
          <HeroCallbackForm
            defaultInterest={interest}
            formKey={formKey}
            title={`Get a Free ${label} Call`}
            subtitle="Tell us where to reach you. A specialist calls within one business day. Free, no obligation."
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceCallbackBand;
